import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { resolveActiveLabels } from 'dashboard/helper/labelHelper';

/**
 * Composable for managing conversation labels
 * @returns {Object} An object containing methods and computed properties for conversation labels
 */
export function useConversationLabels() {
  const store = useStore();
  const getters = useStoreGetters();

  /**
   * The currently selected chat
   * @type {import('vue').ComputedRef<Object>}
   */
  const currentChat = computed(() => getters.getSelectedChat.value);

  /**
   * The ID of the current conversation
   * @type {import('vue').ComputedRef<number|null>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * All labels available for the account
   * @type {import('vue').ComputedRef<Array>}
   */
  const accountLabels = computed(() => getters['labels/getLabels'].value);

  /**
   * Labels currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedLabels = computed(() => {
    return store.getters['conversationLabels/getConversationLabels'](
      conversationId.value
    );
  });

  const pendingLabelTitles = computed(() =>
    store.getters['conversationLabels/getPendingLabels'](conversationId.value)
  );

  const updatingLabelTitles = computed(() =>
    store.getters['conversationLabels/getUpdatingLabels'](conversationId.value)
  );

  const visibleLabelTitles = computed(
    () => pendingLabelTitles.value ?? savedLabels.value
  );

  /**
   * Labels currently active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const activeLabels = computed(() =>
    resolveActiveLabels(visibleLabelTitles.value, accountLabels.value)
  );

  /**
   * Canonical titles shown as selected (catalog titles, including repaired comma-split tags)
   * @type {import('vue').ComputedRef<string[]>}
   */
  const selectedLabelTitles = computed(() =>
    activeLabels.value.map(label => label.title)
  );

  /**
   * Labels available but not active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const inactiveLabels = computed(() => {
    const selected = new Set(selectedLabelTitles.value);
    return accountLabels.value.filter(({ title }) => !selected.has(title));
  });

  /**
   * Updates the labels for the current conversation
   * @param {string[]} selectedLabels - Array of label titles to be set for the conversation
   * @returns {Promise<void>}
   */
  const onUpdateLabels = async selectedLabels => {
    await store.dispatch('conversationLabels/update', {
      conversationId: conversationId.value,
      labels: selectedLabels,
    });
  };

  /**
   * Adds a label to the current conversation
   * @param {Object} value - The label object to be added
   * @param {string} value.title - The title of the label to be added
   */
  const addLabelToConversation = value => {
    const title = value?.title;
    if (!title || updatingLabelTitles.value.includes(title)) return;

    const result = [...visibleLabelTitles.value];
    if (!result.includes(title)) result.push(title);
    onUpdateLabels(result);
  };

  /**
   * Removes a label from the current conversation
   * @param {string} value - The title of the label to be removed
   */
  const removeLabelFromConversation = value => {
    if (updatingLabelTitles.value.includes(value)) return;

    const result = visibleLabelTitles.value.filter(label => label !== value);
    onUpdateLabels(result);
  };

  return {
    accountLabels,
    savedLabels,
    activeLabels,
    selectedLabelTitles,
    inactiveLabels,
    updatingLabelTitles,
    addLabelToConversation,
    removeLabelFromConversation,
    onUpdateLabels,
  };
}
